<template>
    <perfect-scrollbar ref="scrollbar"
                       class="aside-menu kt-grid__item kt-grid__item--fluid"
                       :options="{ suppressScrollX: true }">
        <ul class="aside-menu__nav">
            <template v-for="item in visibleMenu">
                <!-- Leaf item -->
                <li v-if="!item.children"
                    :key="item.key"
                    class="aside-menu__item"
                    :class="{ 'aside-menu__item--active': isActive(item) }">
                    <router-link :to="{ name: item.route }"
                                 class="aside-menu__link"
                                 @click.native="onNavigate">
                        <img :src="item.icon" alt="" class="aside-menu__icon">
                        <span class="aside-menu__text">{{ $t(item.label) }}</span>
                    </router-link>
                </li>

                <!-- Group item with submenu -->
                <li v-else
                    :key="item.key"
                    class="aside-menu__item aside-menu__item--group"
                    :class="{ 'aside-menu__item--active': isActive(item), 'aside-menu__item--open': isOpen(item) }">
                    <button type="button"
                            class="aside-menu__link aside-menu__toggle"
                            :aria-expanded="isOpen(item) ? 'true' : 'false'"
                            @click="toggle(item)">
                        <img :src="item.icon" alt="" class="aside-menu__icon">
                        <span class="aside-menu__text">{{ $t(item.label) }}</span>
                        <i class="aside-menu__arrow glyphicons glyphicons-chevron-down"></i>
                    </button>
                    <transition :css="false"
                                @enter="expandEnter" @after-enter="expandAfterEnter"
                                @leave="expandLeave" @after-leave="updateScroll">
                        <ul v-show="isOpen(item)" class="aside-menu__submenu">
                            <li v-for="child in visibleChildren(item)"
                                :key="child.key"
                                class="aside-menu__subitem"
                                :class="{ 'aside-menu__subitem--active': isActive(child) }">
                                <router-link :to="{ name: child.route }"
                                             class="aside-menu__sublink"
                                             @click.native="onNavigate">
                                    <img :src="child.icon" alt="" class="aside-menu__icon aside-menu__icon--sm">
                                    <span class="aside-menu__text aside-menu__text--sm">{{ $t(child.label) }}</span>
                                </router-link>
                            </li>
                        </ul>
                    </transition>
                </li>
            </template>

            <!-- Logout -->
            <li class="aside-menu__item aside-menu__item--logout">
                <a href="#" role="button" class="aside-menu__link" @click.prevent="logout">
                    <img src="../../assets/icons/icone-sair.svg" alt="" class="aside-menu__icon">
                    <span class="aside-menu__text">{{ $t('AppAsideMenu.Terminar') }}</span>
                </a>
            </li>
        </ul>
    </perfect-scrollbar>
</template>

<script>
import {mapGetters} from "vuex";
import hasFeatureEnabled from "@/mixins/OrganizationFeatureFlagsMixin";
import {router} from "@/router";
import auth from "@/services/auth";

const icon = name => require(`../../assets/icons/${name}.svg`);

export default {
    mixins: [hasFeatureEnabled],
    data() {
        return {
            // key of the currently expanded submenu group (only one open at a time)
            openKey: null,
            showMapOverlay: process.env.VUE_APP_SHOW_MAP_OVERLAY === 'true'
        };
    },
    computed: {
        ...mapGetters(['hasAdminRoles', 'getUserEmail']),
        /**
         * Single source of truth for the menu. Each item:
         *  - key:     unique id used for open/closed tracking
         *  - label:   i18n key under "AppAsideMenu"
         *  - icon:    resolved asset url
         *  - route:   vue-router route name to navigate to
         *  - match:   route names that mark this item active (defaults to [route])
         *  - feature: feature flag required to show the item (optional)
         *  - mapOverlay: only shown when the map-overlay env flag is on (optional)
         *  - children: submenu items (same shape, minus children)
         */
        menu() {
            return [
                {
                    key: 'track', label: 'AppAsideMenu.Localização', icon: icon('icone-mapa'),
                    route: 'home', match: ['home', 'vehicles-track'], feature: 'TRACK'
                },
                {
                    key: 'fleet-report', label: 'AppAsideMenu.Relatório da Frota', icon: icon('icone-fleet-report'),
                    route: 'fleet-report', mapOverlay: true
                },
                {
                    key: 'analysis', label: 'AppAsideMenu.Análises', icon: icon('icone-relatorios-1'),
                    route: 'analysis', feature: 'ANALYSIS'
                },
                // {
                //     key: 'dashboard', label: 'AppAsideMenu.Painel', icon: icon('bar-chart'),
                //     route: 'dashboard', feature: 'DASHBOARD'
                // },
                {
                    key: 'agriculture', label: 'AppAsideMenu.Agro', icon: icon('icone-agro'), feature: 'AGRICULTURE',
                    children: [
                        {
                            key: 'agro-analysis', label: 'AppAsideMenu.Análise', icon: icon('icone-agro-analise'),
                            route: 'agriculture-operations'
                        },
                        {
                            key: 'agro-operations', label: 'AppAsideMenu.Operações', icon: icon('icone-agro-settings'),
                            route: 'agriculture-work-analyzer'
                        },
                        {
                            key: 'agro-fields', label: 'AppAsideMenu.Talhões', icon: icon('icone-talhoes'),
                            route: 'fields', feature: 'FIELDS'
                        },
                        // {
                        //     key: 'agro-files', label: 'AppAsideMenu.Arquivos', icon: icon('icone-arquivo'),
                        //     route: 'agriculture-export-history'
                        // }
                    ]
                },
                {
                    key: 'roads', label: 'AppAsideMenu.Estradas', icon: icon('icone-estrada'),
                    route: 'anomalias', feature: 'ROADS'
                },
                {
                    key: 'operators', label: 'AppAsideMenu.Motoristas', icon: icon('icone-operador'),
                    route: 'operators', match: ['operators', 'operators-edit'], feature: 'OPERATORS'
                },
                {
                    key: 'vehicles', label: 'AppAsideMenu.Veículos', icon: icon('icone-maquinario'),
                    route: 'vehicles', match: ['vehicles', 'vehicles-new', 'vehicles-edit', 'vehicles-group'],
                    feature: 'VEHICLES'
                },
                {
                    key: 'implements', label: 'AppAsideMenu.Implementos', icon: icon('icone-implemento'),
                    route: 'implements', match: ['implements', 'new-implement', 'edit-implement'],
                    feature: 'ORGANIZATION_IMPLEMENTS'
                },
                {
                    key: 'costs', label: 'AppAsideMenu.Custos', icon: icon('icone-costs'),
                    route: 'costs', feature: 'COSTS'
                },
                {
                    key: 'alerts', label: 'AppAsideMenu.Alertas', icon: icon('icone-alerta'),
                    route: 'alerts-machine', match: ['alerts-machine', 'alerts-orders', 'alerts-dictionary'],
                    feature: 'ALERTS'
                },
                // {
                //     key: 'alerts-coa', label: 'AppAsideMenu.Alertas COA', icon: icon('icone-coa-alert'),
                //     feature: 'PARAMETRIC_FAILURES',
                //     children: [
                //         {
                //             key: 'coa', label: 'AppAsideMenu.COA', icon: icon('icone-coa-real-time'),
                //             route: 'alerts-coa'
                //         },
                //         {
                //             key: 'coa-historical', label: 'AppAsideMenu.Histórico', icon: icon('icone-coa-historical'),
                //             route: 'alerts-historical'
                //         },
                //         {
                //             key: 'coa-sensor', label: 'AppAsideMenu.Sensores', icon: icon('icone-coa-sensor'),
                //             route: 'alerts-sensor'
                //         },
                //         {
                //             key: 'coa-config', label: 'AppAsideMenu.Alertas Configurados', icon: icon('icone-coa-alert'),
                //             route: 'alerts-coa-alerts'
                //         },
                //         {
                //             key: 'coa-machine-report', label: 'AppAsideMenu.Relatório dos dados de máquina',
                //             icon: icon('icone-coa-historical'), route: 'machine-report-coa'
                //         }
                //     ]
                // },
                {
                    key: 'devices', label: 'AppAsideMenu.Dipositivos', icon: icon('icone-dispositivo'),
                    route: 'devices', feature: 'DEVICES'
                },
                {
                    key: 'beacons', label: 'AppAsideMenu.Beacons', icon: icon('icone-beacon'),
                    route: 'beacons', feature: 'BEACONS'
                },
                {
                    key: 'users', label: 'AppAsideMenu.Organizações', icon: icon('icone-organization'),
                    route: 'users', feature: 'USERS'
                },
                {
                    key: 'profile', label: 'AppAsideMenu.Perfil', icon: icon('user-icon'),
                    route: 'profile',
                    match: ['profile', 'profile-account-information', 'profile-change-password']
                },
                {
                    key: 'settings', label: 'AppAsideMenu.Configurações', icon: icon('icone-ajustes'),
                    route: 'settings',
                    match: [
                        'settings', 'webhook', 'machine-report', 'operations-center-connection',
                        'operations-center-connection-callback', 'checklists', 'new-checklist',
                        'new-pre-existent-checklist', 'activities-list', 'apontamentos', 'configuration-list',
                        'new-configuration-list', 'edit-configuration-list', 'fleet-report', 'work-data-upload'
                    ]
                }
            ];
        },
        visibleMenu() {
            return this.menu.filter(item => this.isVisible(item));
        }
    },
    methods: {
        isVisible(item) {
            if (item.mapOverlay) return this.showMapOverlay;
            if (item.feature) return this.hasFeatureEnabled(item.feature);
            return true;
        },
        visibleChildren(item) {
            return (item.children || []).filter(child => this.isVisible(child));
        },
        isActive(item) {
            const names = item.match || [item.route];
            if (names.includes(this.$route.name)) return true;
            if (item.children) return item.children.some(child => this.isActive(child));
            return false;
        },
        isOpen(item) {
            return this.openKey === item.key;
        },
        toggle(item) {
            this.openKey = this.openKey === item.key ? null : item.key;
        },
        // Expand the group that owns the active route, so deep links land open.
        syncOpenGroup() {
            const active = this.visibleMenu.find(item => item.children && this.isActive(item));
            this.openKey = active ? active.key : null;
        },
        onNavigate() {
            // Let the layout close the mobile drawer after a destination is chosen.
            this.$emit('navigate');
        },
        logout() {
            this.$emit('navigate');
            const dataSSO = JSON.parse(localStorage.getItem(process.env.VUE_APP_SSO_LOGIN));
            if (dataSSO) {
                auth.logout();
            } else {
                this.$store.dispatch('logout');
                router.push({name: 'login'});
            }
        },
        updateScroll() {
            this.$nextTick(() => {
                const ps = this.$refs.scrollbar && this.$refs.scrollbar.ps;
                if (ps) ps.update();
            });
        },
        // --- submenu expand/collapse height transition ---
        // css is disabled on the <transition>, so we drive the height animation
        // here and always resolve `done` (with a timer fallback) — that way the
        // submenu can never get stuck collapsed if transitionend doesn't fire.
        expandEnter(el, done) {
            el.style.height = '0';
            // force reflow so the height animates up from 0
            // eslint-disable-next-line no-unused-expressions
            el.offsetHeight;
            el.style.height = `${el.scrollHeight}px`;
            this.onHeightTransitionEnd(el, done);
        },
        expandAfterEnter(el) {
            el.style.height = 'auto';
            this.updateScroll();
        },
        expandLeave(el, done) {
            el.style.height = `${el.scrollHeight}px`;
            // force reflow so the height animates back to 0
            // eslint-disable-next-line no-unused-expressions
            el.offsetHeight;
            el.style.height = '0';
            this.onHeightTransitionEnd(el, done);
        },
        onHeightTransitionEnd(el, done) {
            let finished = false;
            const finish = () => {
                if (finished) return;
                finished = true;
                el.removeEventListener('transitionend', handler);
                done();
            };
            const handler = e => {
                if (e.target === el && e.propertyName === 'height') finish();
            };
            el.addEventListener('transitionend', handler);
            // fallback in case transitionend is throttled or never fires
            setTimeout(finish, 260);
        }
    },
    watch: {
        '$route': {
            handler() {
                this.syncOpenGroup();
            },
            immediate: true
        }
    }
};
</script>

<style scoped lang="scss">

@import "src/assets/styles/constants";

$aside-active: $aside-menu-item-active;

.aside-menu {
    height: 100%;
    /* bound the scroll area below the brand logo so perfect-scrollbar engages */
    max-height: calc(100vh - 81px);
}

@media (max-width: 1024px) {
    .aside-menu {
        max-height: calc(100vh - 50px);
    }
}

.aside-menu__nav {
    list-style: none;
    margin: 0;
    padding: 0;
}

.aside-menu__item {
    position: relative;
}

.aside-menu__link {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 12px 4px;
    border: 0;
    background: transparent;
    text-decoration: none;
    cursor: pointer;
    transition: background-color .15s ease;
}

.aside-menu__icon {
    width: 46px;
    height: 46px;
    object-fit: contain;
    filter: $aside-menu-item-inactive;
    transition: filter .15s ease;
}

.aside-menu__icon--sm {
    width: 34px;
    height: 34px;
}

.aside-menu__text {
    margin-top: 4px;
    font-size: 12px;
    line-height: 1.15;
    text-align: center;
    color: $aside-menu-text;
    word-break: break-word;
}

.aside-menu__text--sm {
    font-size: 11px;
}

/* Hover (desktop only — avoids sticky states on touch) */
@media (min-width: 1025px) {
    .aside-menu__link:hover {
        background-color: $aside-active;
    }

    .aside-menu__link:hover .aside-menu__icon {
        filter: brightness(0) invert(1);
    }

    .aside-menu__link:hover .aside-menu__text {
        color: white;
    }
}

/* Active state */
.aside-menu__item--active > .aside-menu__link {
    background-color: $aside-active;
}

.aside-menu__item--active > .aside-menu__link .aside-menu__icon {
    filter: brightness(0) invert(1);
}

.aside-menu__item--active > .aside-menu__link .aside-menu__text {
    color: white;
}

/* Group toggle arrow */
.aside-menu__arrow {
    display: block;
    margin-top: 3px;
    font-size: 10px;
    line-height: 1;
    color: gray;
    transition: transform .2s ease;
}

.aside-menu__item--open .aside-menu__arrow {
    transform: rotate(180deg);
}

.aside-menu__item--active .aside-menu__arrow {
    color: white;
}

/* Submenu (inline, expandable) */
.aside-menu__submenu {
    list-style: none;
    margin: 0;
    padding: 0;
    overflow: hidden;
    background-color: $aside-submenu-background;
    transition: height .2s ease;
}

.aside-menu__sublink {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 10px 4px;
    text-decoration: none;
    transition: background-color .15s ease;
}

.aside-menu__subitem--active .aside-menu__sublink {
    background-color: $aside-active;
}

.aside-menu__subitem--active .aside-menu__icon {
    filter: brightness(0) invert(1);
}

.aside-menu__subitem--active .aside-menu__text {
    color: white;
}

@media (min-width: 1025px) {
    .aside-menu__sublink:hover {
        background-color: $aside-active;
    }

    .aside-menu__sublink:hover .aside-menu__icon {
        filter: brightness(0) invert(1);
    }

    .aside-menu__sublink:hover .aside-menu__text {
        color: white;
    }
}

.aside-menu__item--logout {
    border-top: 1px solid rgba(0, 0, 0, .06);
}

</style>
